import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { useOrder } from '../../contexts/OrderContext'
import { useTheme } from '../../contexts/ThemeContext'
import { FixedHeader, GobackTitle } from '../Layout/FixedHeader'
import { FixedFooter } from '../Layout/FixedFooter'
import { Button } from '../Shared/Buttons'
import { CheckoutProgress } from '../CheckoutProgress'
import ApiService from '../../service/ApiService'
import { getDeviceId } from '../../utils/deviceId'
import { useCart } from '../../contexts/CartContext'
import { useConfig } from '../../contexts/ConfigContext'
import { useAddress } from '../../contexts/AddressContext'
import { useCurrentVendor } from '../../hooks/useCurrentVendor'
import { getVendorDeliveryFee } from '../../utils/vendor'
import { getAppliedCouponDiscount, getCartSubTotal } from '../../utils/coupon'
import { PaymentType } from './PaymentType'
import { Loading } from '../Shared/Loading'
import {
  PaymentContainer,
  PaymentBody,
  SectionTitle,
  SectionHint,
  AmountBlock,
  PaymentConfigNotice
} from './styles'

const GATEWAY_PAYMENTS = ['knet', 'credit card', 'applepay']

const isGatewayPayment = (payment) => GATEWAY_PAYMENTS.includes(payment)

const isGatewayConfigReady = (configs) => Boolean(
  configs?.response_url &&
  configs?.failure_url
)

const formatDeliveryTime = (time) => {
  if (!time) return time
  const parsed = moment(time, 'ha', true)
  return parsed.isValid() ? parsed.format('h:mm A') : time
}

const formatAmount = (amount) => {
  const n = Number(amount)
  if (Number.isNaN(n)) return '0.000'
  return n.toFixed(3)
}

const resolvePaymentCodes = (payment) => {
  if (payment === 'cash') return { orderType: 0, gatewayType: 0 }
  if (payment === 'knet') return { orderType: 1, gatewayType: 1 }
  // 13 = KNET Apple Pay International (credit + debit)
  if (payment === 'applepay') return { orderType: 2, gatewayType: 13 }
  return { orderType: 2, gatewayType: 2 }
}

const getMerchantDomain = () => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname.replace(/^www\./, '')
  }
  return 'althogsweet.com'
}

export const Payment = () => {
  const currentLng = localStorage.getItem('i18nextLng')
  const [orderDetail, { updateOrder }] = useOrder()
  const [configState, { refreshConfigs }] = useConfig()
  const [theme] = useTheme()
  const [, cities] = useAddress()
  const vendor = useCurrentVendor()
  const [cart, { currentVendorId }] = useCart()
  const history = useHistory()
  const { t } = useTranslation()
  const [payment, setPayment] = useState(orderDetail.payment)
  const [loading, setLoading] = useState(false)
  const [paymentConfigReady, setPaymentConfigReady] = useState(false)

  useEffect(() => {
    let active = true

    const loadPaymentConfig = async () => {
      setPaymentConfigReady(false)
      try {
        await refreshConfigs()
      } catch (err) {
        console.log(err, 'payment config error')
      } finally {
        if (active) {
          setPaymentConfigReady(true)
        }
      }
    }

    loadPaymentConfig()

    return () => {
      active = false
    }
  }, [refreshConfigs])

  const configs = configState?.configs || {}
  const isConfigLoading = configState?.loading || !paymentConfigReady
  const requiresGateway = isGatewayPayment(payment)
  const gatewayReady = isGatewayConfigReady(configs)
  const canSubmit = Boolean(
    payment &&
    !loading &&
    !isConfigLoading &&
    (!requiresGateway || gatewayReady)
  )

  const subTotal = getCartSubTotal(cart)
  const currentCity = cities.find(obj => obj.id === orderDetail?.currentLocation)
  const deliveryFee = getVendorDeliveryFee(vendor, currentCity)
  const _coupon = getAppliedCouponDiscount(orderDetail, subTotal)
  const couponId = orderDetail?.coupon?.couponId
  const TotalAmount = subTotal + deliveryFee - _coupon
  const currency = configs?.currency || 'KWD'

  const handlePayment = (val) => {
    setPayment(val)
  }

  const creatOrder = async (orderType, gatewayType) => {
    const orderReferenceNumber = new Date().getTime()
    const unitType = orderDetail?.unitType === 'house' ? 1 :
      orderDetail?.unitType === 'apartment' ? 2 : 3
    const unitNumber = orderDetail?.unitType === 'house' ? orderDetail?.address?.house_no :
      orderDetail?.unitType === 'apartment' ? orderDetail?.address?.apartment_no : orderDetail?.address?.office_no

    const orderUrl = '/new_order'
    const orderObj = {
      orderReferenceNumber: orderReferenceNumber,
      name: orderDetail?.contact?.name,
      phone: orderDetail?.contact?.phone,
      total: TotalAmount,
      total_price: cart.map(ca => ca.price * ca.count),
      payment_type: orderType,
      delivery_charge: Number(deliveryFee).toFixed(1),
      delivery_date: orderDetail?.deiveryTime?.date,
      delivery_time: formatDeliveryTime(orderDetail?.deiveryTime?.time),
      vendor_id: currentVendorId,
      zone_id: orderDetail?.currentLocation,
      unit_type: unitType,
      unit_number: Number(unitNumber),
      state: orderDetail?.currentState || 1,
      avenue: Number(orderDetail?.address?.avenue || ''),
      city: orderDetail?.currentLocation || 1,
      block: Number(orderDetail?.address?.block),
      street: orderDetail?.address?.street || '',
      building: Number(orderDetail?.address?.building) || null,
      floor: Number(orderDetail?.address?.floor) || null,
      additional: orderDetail?.address?.additional || '',
      product_id: cart.map(ca => ca.prodId),
      price: cart.map(ca => ca.price),
      qty: cart.map(ca => ca.count),
      sub_total: subTotal,
      device_id: getDeviceId(),
    }

    if (couponId) {
      orderObj.coupon_id = couponId
    } else {
      orderObj.discount = _coupon
    }

    try {
      setLoading(true)
      const result = await ApiService(orderUrl, orderObj, 'post')
      if (result?.success === '1' && result?.order_id) {
        try {
          createPayment(gatewayType, result?.order_id, orderReferenceNumber)
        } catch (error) {
          setLoading(false)
          console.log(error, 'payResult error')
        }
        return
      }
      console.log('faild order')
      setLoading(false)
    } catch (err) {
      console.log(err, 'order error')
      setLoading(false)
    }
  }

  const createPayment = async (gatewayType, orderId, orderReferenceNumber) => {
    const paymentUrl = '/payment'
    if (gatewayType === 0) {
      const changes = {
        orderInfo: {
          paymentStatus: true,
          orderReferenceNumber: orderReferenceNumber,
          amount: TotalAmount,
          method: 0,
          variable1: orderId,
          variable2: subTotal,
          variable3: deliveryFee,
          variable4: _coupon
        }
      }
      updateOrder(changes)
      setLoading(false)
      history.push('./confirm?data=cashpay')
      return
    }

    if (gatewayType !== 0 && !isGatewayConfigReady(configState?.configs)) {
      setLoading(false)
      return
    }

    const isApplePay = gatewayType === 13
    const paymentObj = {
      orderReferenceNumber: orderReferenceNumber,
      amount: TotalAmount,
      paymentType: gatewayType,
      currency: configState?.configs?.currency || 'KWD',
      responseUrl: configState?.configs?.response_url,
      failureUrl: configState?.configs?.failure_url,
      variable1: orderId,
      variable2: isApplePay ? 'applepay' : subTotal,
      variable3: deliveryFee,
      variable4: _coupon,
      variable5: isApplePay ? getMerchantDomain() : '',
      applePay: isApplePay
    }

    try {
      const result = await ApiService(paymentUrl, paymentObj, 'post')
      if (result?.success && result?.url) {
        window.location.href = result.url
        return
      }
      setLoading(false)
      console.log(result, 'payment redirect error')
    } catch (err) {
      console.log(err, 'error')
      setLoading(false)
    }
  }

  const goConfirm = () => {
    if (!canSubmit) return

    if (payment) {
      updateOrder({ payment })
      const { orderType, gatewayType } = resolvePaymentCodes(payment)
      creatOrder(orderType, gatewayType)
    }
  }

  const goBack = () => {
    history.goBack()
  }

  return (
    <>
      {(loading || isConfigLoading) && <Loading color={theme.colors.loadingCol} />}
      <PaymentContainer>
        <FixedHeader>
          {currentLng === 'en'
            ? <FiArrowLeft size='20' onClick={goBack} />
            : <FiArrowRight size='20' onClick={goBack} />
          }
          <GobackTitle>{t('Checkout')}</GobackTitle>
        </FixedHeader>
        <CheckoutProgress step={3} />
        <PaymentBody>
          <SectionTitle>{t('Payment Method')}</SectionTitle>
          <SectionHint>{t('Choose-payment')}</SectionHint>

          <AmountBlock>
            <div className='label'>{t('Amount Due')}</div>
            <div className='value'>
              {formatAmount(TotalAmount)}
              <span className='currency'>{currency}</span>
            </div>
          </AmountBlock>

          <PaymentType payment={payment} handlePayment={handlePayment} />

          {requiresGateway && paymentConfigReady && !gatewayReady && (
            <PaymentConfigNotice>{t('Online payment unavailable')}</PaymentConfigNotice>
          )}
        </PaymentBody>
        <FixedFooter>
          <Button color='primary' width='100%' onClick={goConfirm} disabled={!canSubmit}>
            {loading ? t('Please wait') : t('Pay Now')}
          </Button>
        </FixedFooter>
      </PaymentContainer>
    </>
  )
}
